/**
 * Module Bảo vệ An ninh & Chống dịch ngược (Security Guard & Anti-DevTools / Anti-Debugger)
 * 
 * Cơ chế hoạt động:
 * 1. Môi trường Localhost: Cho phép F12, chuột phải, console log thoải mái để lập trình.
 * 2. Môi trường Production:
 *    - Chặn phím tắt mở DevTools (F12, Ctrl+Shift+I, J, C, U, S).
 *    - Chặn menu chuột phải chống inspect.
 *    - Vòng lặp Anti-Debugger ngầm làm tê liệt DevTools nếu cố tình mở.
 *    - Tự động kích hoạt Boss Key ngụy trang về Excel nếu phát hiện mở DevTools.
 *    - Vô hiệu hóa log console.
 * 3. Mở khóa bí mật (Zero-Knowledge SHA-256 Hash):
 *    - Không chứa mật khẩu gốc trên code FE đã đẩy lên (chỉ chứa chuỗi mã băm SHA-256 1 chiều).
 *    - Không dùng query param dễ đoán (?debug_mode / ?dev).
 *    - Mở khóa bằng cách CLICK NHANH 5 LẦN VÀO LOGO (hoặc phím tắt Ctrl+Alt+Shift+F12) và nhập mã bí mật.
 *    - Hỗ trợ mở khóa qua URL bí mật ?k=MA_BI_MAT (tự động xóa khỏi URL ngay sau khi xác thực để không lưu lịch sử).
 */
import { ADMIN_SHA256_HASH } from '../config/securityHash.js';

/**
 * Hàm tính mã băm SHA-256 chuẩn của chuỗi ký tự bằng Web Crypto API
 * @param {string} text 
 * @returns {Promise<string>} Chuỗi hex 64 ký tự
 */
async function computeSha256(text) {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (err) {
    return '';
  }
}

export class SecurityGuard {
  constructor(options = {}) {
    this.onPanicTrigger = options.onPanicTrigger || null;
    this.isLocal = this.checkIsLocalEnvironment();
    this.isUnlocked = this.checkIsUnlocked();

    this.debuggerTimer = null;
    this.detectorTimer = null;
    this.isDevToolsDetected = false;

    // Biến đếm nhịp click bí mật vào logo
    this.logoClickCount = 0;
    this.logoClickResetTimer = null;

    // Tham chiếu các event handler để có thể tháo gỡ khi mở khóa
    this.keydownHandler = null;
    this.contextmenuHandler = null;
  }

  /**
   * Kiểm tra xem đang chạy ở localhost hay không
   * @returns {boolean}
   */
  checkIsLocalEnvironment() {
    const host = window.location.hostname;
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '[::1]' ||
      host.startsWith('192.168.') ||
      host.startsWith('10.') ||
      host.endsWith('.local') ||
      host === 'badinh-dev.pages.dev' ||
      host.endsWith('.badinh-dev.pages.dev') ||
      window.location.protocol === 'file:'
    );
  }

  /**
   * Kiểm tra xem thiết bị này đã được mở khóa bằng mã bí mật SHA-256 trước đó chưa
   * @returns {boolean}
   */
  checkIsUnlocked() {
    try {
      const storedToken = localStorage.getItem('__sec_admin_token__') || sessionStorage.getItem('__sec_admin_token__');
      return storedToken === ADMIN_SHA256_HASH;
    } catch (e) {
      return false;
    }
  }

  /**
   * Khởi động hệ thống an ninh
   */
  async init() {
    // 1. Kiểm tra mở khóa qua tham số URL bí mật ?k=... hoặc ?secret=...
    await this.checkSecretUrlParam();

    // 2. Nếu ở Localhost hoặc đã mở khóa hợp lệ -> Bật chế độ tự do, không chặn
    if (this.isLocal || this.isUnlocked) {
      console.log(
        '%c[Security Guard] %cĐang ở chế độ QUẢN TRỊ VIÊN / LOCALHOST. F12 và DevTools được mở tự do.',
        'color: #10b981; font-weight: bold;',
        'color: #374151;'
      );
      // Vẫn gắn bộ lắng nghe khóa lại nếu cần
      this.bindSecretTriggers();
      return;
    }

    // 3. Trên môi trường Production chưa mở khóa: Kích hoạt toàn diện các lá chắn
    this.bindHotkeyBlocker();
    this.bindContextMenuBlocker();
    this.startAntiDebuggerLoop();
    this.startDevToolsDetector();
    this.disableConsoleOutput();

    // 4. Kích hoạt bộ lắng nghe cử chỉ mở khóa bí mật
    this.bindSecretTriggers();
  }

  /**
   * Kiểm tra tham số bí mật trên URL nếu có
   * Sau khi kiểm tra thành công, lập tức XÓA KHỎI URL để không để lại dấu vết trong lịch sử trình duyệt!
   */
  async checkSecretUrlParam() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const secretCandidate = urlParams.get('k') || urlParams.get('secret') || urlParams.get('key');
      if (!secretCandidate) return;

      const candidateHash = await computeSha256(secretCandidate.trim());
      if (candidateHash === ADMIN_SHA256_HASH) {
        this.unlockAdminMode();

        // Xóa sạch param bí mật khỏi URL trên thanh địa chỉ ngay tức khắc
        urlParams.delete('k');
        urlParams.delete('secret');
        urlParams.delete('key');
        const newSearch = urlParams.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
        window.history.replaceState({}, document.title, newUrl);
      }
    } catch (e) {}
  }

  /**
   * Gắn các bộ kích hoạt mở khóa bí mật (Secret Triggers):
   * 1. Bấm nhanh 5 lần vào bất kỳ Logo nào trên trang (Google Sheets, CapCut, Canva, Gmail, Zalo).
   * 2. Phím tắt ẩn: Ctrl + Alt + Shift + F12
   */
  bindSecretTriggers() {
    // 1. Phím tắt mở khóa bí mật Ctrl + Alt + Shift + F12
    window.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.altKey && e.shiftKey && (e.key === 'F12' || e.keyCode === 123)) {
        e.preventDefault();
        e.stopPropagation();
        this.promptAdminPassword();
      }
    }, true);

    // 2. Gắn sự kiện click 5 lần vào Logo
    const logoSelectors = [
      '.sheets-logo-box',
      '.cc-logo',
      '.cv-logo',
      '.gmail-logo',
      '.zalo-logo',
      '#cc-preview-title'
    ];

    const attachLogoClick = () => {
      logoSelectors.forEach(sel => {
        const els = document.querySelectorAll(sel);
        els.forEach(el => {
          if (el.dataset.secBound) return;
          el.dataset.secBound = 'true';
          el.style.cursor = 'pointer';
          el.addEventListener('click', (e) => this.handleLogoClick(e));
        });
      });
    };

    attachLogoClick();
    // Gắn thêm sau khi DOM hoàn tất hoặc đổi giao diện
    setTimeout(attachLogoClick, 1000);
    window.addEventListener('theme-changed', () => setTimeout(attachLogoClick, 500));
  }

  /**
   * Xử lý nhịp click 5 lần liên tiếp trong 2.5 giây vào Logo
   */
  handleLogoClick(e) {
    this.logoClickCount += 1;

    // Reset bộ đếm nếu không click tiếp trong 2.5s
    clearTimeout(this.logoClickResetTimer);
    this.logoClickResetTimer = setTimeout(() => {
      this.logoClickCount = 0;
    }, 2500);

    // Đủ 5 lần -> Mở hộp thoại nhập mã bí mật
    if (this.logoClickCount >= 5) {
      this.logoClickCount = 0;
      clearTimeout(this.logoClickResetTimer);
      this.promptAdminPassword();
    }
  }

  /**
   * Hiển thị hộp thoại nhập mã bí mật quản trị viên
   */
  async promptAdminPassword() {
    // Nếu đã mở khóa rồi: Cho phép chọn khóa lại
    if (this.isUnlocked) {
      const lockAgain = confirm('[Quản trị viên] Bạn đang ở chế độ MỞ KHÓA DevTools.\nBạn có muốn KHÓA LẠI hệ thống bảo mật không?');
      if (lockAgain) {
        this.lockAdminMode();
        alert('🔒 Đã khóa lại hệ thống an ninh thành công!');
      }
      return;
    }

    const inputKey = prompt('🔐 [BẢO MẬT HỆ THỐNG]\nVui lòng nhập mã bí mật quản trị viên để mở khóa F12:');
    if (!inputKey) return;

    const inputHash = await computeSha256(inputKey.trim());
    if (inputHash === ADMIN_SHA256_HASH) {
      this.unlockAdminMode();
      alert('✓ XÁC THỰC THÀNH CÔNG!\nF12, phím tắt Inspect và DevTools đã được mở khóa tự do trên trình duyệt này.');
    } else {
      alert('❌ Mã bí mật không chính xác!');
      // Kích hoạt Boss Key ngụy trang ngay lập tức để phòng thủ!
      this.handleDevToolsAttempt('Sai mã quản trị viên');
    }
  }

  /**
   * Mở khóa chế độ Quản trị viên (Admin Mode)
   */
  unlockAdminMode() {
    this.isUnlocked = true;
    try {
      localStorage.setItem('__sec_admin_token__', ADMIN_SHA256_HASH);
      sessionStorage.setItem('__sec_admin_token__', ADMIN_SHA256_HASH);
    } catch (e) {}

    // Dừng vòng lặp Anti-Debugger và DevTools Detector
    if (this.debuggerTimer) clearTimeout(this.debuggerTimer);
    if (this.detectorTimer) clearInterval(this.detectorTimer);

    // Tháo gỡ các bộ chặn phím tắt và chuột phải
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler, true);
    }
    if (this.contextmenuHandler) {
      window.removeEventListener('contextmenu', this.contextmenuHandler, true);
    }

    // Khôi phục lại console
    delete window.console.log;
    delete window.console.info;
    delete window.console.warn;
    delete window.console.error;
    delete window.console.debug;
  }

  /**
   * Khóa lại chế độ bảo vệ Production
   */
  lockAdminMode() {
    this.isUnlocked = false;
    try {
      localStorage.removeItem('__sec_admin_token__');
      sessionStorage.removeItem('__sec_admin_token__');
    } catch (e) {}
    window.location.reload();
  }

  /**
   * Chặn phím tắt mở DevTools
   */
  bindHotkeyBlocker() {
    this.keydownHandler = (e) => {
      if (this.isUnlocked) return;

      // 1. Phím F12
      if (e.key === 'F12' || e.keyCode === 123) {
        e.preventDefault();
        e.stopPropagation();
        this.handleDevToolsAttempt('Phím F12');
        return false;
      }

      // 2. Ctrl + Shift + I (Inspect)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.keyCode === 73)) {
        e.preventDefault();
        e.stopPropagation();
        this.handleDevToolsAttempt('Ctrl+Shift+I');
        return false;
      }

      // 3. Ctrl + Shift + J (Console)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'J' || e.key === 'j' || e.keyCode === 74)) {
        e.preventDefault();
        e.stopPropagation();
        this.handleDevToolsAttempt('Ctrl+Shift+J');
        return false;
      }

      // 4. Ctrl + Shift + C (Inspect Element)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'C' || e.key === 'c' || e.keyCode === 67)) {
        e.preventDefault();
        e.stopPropagation();
        this.handleDevToolsAttempt('Ctrl+Shift+C');
        return false;
      }

      // 5. Ctrl + U (View Source)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'U' || e.key === 'u' || e.keyCode === 85)) {
        e.preventDefault();
        e.stopPropagation();
        this.handleDevToolsAttempt('Ctrl+U');
        return false;
      }

      // 6. Ctrl + S (Save Page)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'S' || e.key === 's' || e.keyCode === 83)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    window.addEventListener('keydown', this.keydownHandler, true);
  }

  /**
   * Chặn menu chuột phải chống inspect
   */
  bindContextMenuBlocker() {
    this.contextmenuHandler = (e) => {
      if (this.isUnlocked) return;

      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') {
        return true;
      }

      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    window.addEventListener('contextmenu', this.contextmenuHandler, true);
  }

  /**
   * Vòng lặp Anti-Debugger liên tục
   */
  startAntiDebuggerLoop() {
    const triggerDebugger = () => {
      if (this.isUnlocked) return;
      try {
        const dbg = new Function('debugger');
        dbg();
      } catch (err) {}
    };

    const recursiveDbg = () => {
      if (this.isUnlocked) return;
      triggerDebugger();
      this.debuggerTimer = setTimeout(recursiveDbg, 250);
    };

    setTimeout(recursiveDbg, 500);
  }

  /**
   * Bộ giám sát phát hiện DevTools đang mở
   */
  startDevToolsDetector() {
    const checkDevTools = () => {
      if (this.isUnlocked) return;

      const widthDiff = window.outerWidth - window.innerWidth > 160;
      const heightDiff = window.outerHeight - window.innerHeight > 160;

      let timingDetected = false;
      const start = performance.now();
      try {
        const checkFn = new Function('debugger');
        checkFn();
      } catch (e) {}
      const duration = performance.now() - start;

      if (duration > 100) {
        timingDetected = true;
      }

      if (widthDiff || heightDiff || timingDetected) {
        if (!this.isDevToolsDetected) {
          this.isDevToolsDetected = true;
          this.handleDevToolsAttempt('Phát hiện cửa sổ DevTools đang hoạt động');
        }
      } else {
        this.isDevToolsDetected = false;
      }
    };

    this.detectorTimer = setInterval(checkDevTools, 1000);
  }

  /**
   * Xử lý khi phát hiện cố tình mở DevTools: Tự động kích hoạt Boss Key ngụy trang
   */
  handleDevToolsAttempt(reason) {
    if (this.isLocal || this.isUnlocked) return;

    try {
      console.clear();
    } catch (e) {}

    if (typeof this.onPanicTrigger === 'function') {
      this.onPanicTrigger();
    } else if (window.bossKeyModule && typeof window.bossKeyModule.toggle === 'function') {
      window.bossKeyModule.toggle();
    }
  }

  /**
   * Vô hiệu hóa log console trên Production
   */
  disableConsoleOutput() {
    if (this.isLocal || this.isUnlocked) return;
    try {
      const emptyFn = () => {};
      console.log = emptyFn;
      console.info = emptyFn;
      console.warn = emptyFn;
      console.debug = emptyFn;
      console.error = emptyFn;
    } catch (e) {}
  }
}
